import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { snapshotsCompare } from "@/modules/snapshots/snapshotsCompare";
import { useAppSelector } from "@/store/hooks";
import { selectCompareResults } from "@/store/slices/snapshotCompareSlice";
import { ulid } from "ulid";

interface FormInputs {
  user_prompt: string;
}

interface Props {
  setConversationId: (conversationId: string) => void;
}

export const GridSidebarComparisonForm: React.FC<Props> = ({ setConversationId }) => {
  const [submitState, setSubmitState] = React.useState<"idle" | "submitting" | "complete">("idle");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      user_prompt:
        "Compare the input texts and provide a detailed comparison highlighting the similarities and differences",
    },
  });

  const results = useAppSelector(selectCompareResults);

  const onSubmit = async (data: FormInputs) => {
    setSubmitState("submitting");
    const conversationId = ulid();
    setConversationId(conversationId);

    await snapshotsCompare(
      data.user_prompt,
      conversationId,
      results.map(row => ({ documentName: row.documentName, text: row.result })),
    );
    setSubmitState("complete");
  };

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <textarea
            {...register("user_prompt", { required: "Prompt is required" })}
            placeholder="Enter your comparison prompt..."
            className="w-full resize-none rounded-lg border border-[#e1ddd6] bg-beige p-3 text-sm focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed"
            rows={4}
            disabled={submitState !== "idle"}
          />
          {errors.user_prompt && <p className="mt-1 text-sm text-red-500">{errors.user_prompt.message}</p>}
        </div>
        <div className="text-right">
          <Button type="submit" disabled={submitState !== "idle"} variant="coral">
            {submitState === "idle" && "Compare"}
            {submitState === "submitting" && "Comparing..."}
            {submitState === "complete" && "Compared"}
          </Button>
        </div>
      </form>
    </div>
  );
};
